import { useEffect, useCallback } from 'react';
import { useTranslation } from '../contexts/TranslationContext';

/**
 * Hook for components that load data via API and need translation
 * Automatically triggers translation when data changes or component mounts
 */
const useApiTranslation = (dataState, options = {}) => {
  const { 
    currentLanguage, 
    translateNewContent, 
    isLoading: isTranslating 
  } = useTranslation();
  
  const {
    selector = null,           // CSS selector to target specific element
    delay = 300,              // Delay before triggering translation
    dependencies = [],        // Additional dependencies to watch
    enabled = true           // Whether translation is enabled
  } = options;

  // Manual trigger function
  const triggerTranslation = useCallback(async (customSelector = null) => {
    if (!enabled || currentLanguage === 'en') return;
    
    if (translateNewContent) {
      await translateNewContent(customSelector || selector);
    }
  }, [translateNewContent, currentLanguage, selector, enabled]);

  // Auto-trigger when data changes
  useEffect(() => {
    if (!enabled || currentLanguage === 'en' || !dataState) return;

    const timer = setTimeout(() => {
      triggerTranslation();
    }, delay);

    return () => clearTimeout(timer);
  }, [dataState, currentLanguage, triggerTranslation, delay, enabled, ...dependencies]);

  // Auto-trigger when language changes (for already loaded data)
  useEffect(() => {
    if (!enabled || currentLanguage === 'en' || !dataState) return;

    const timer = setTimeout(() => {
      triggerTranslation();
    }, 100); // Quick trigger for language changes

    return () => clearTimeout(timer);
  }, [currentLanguage]);

  return {
    triggerTranslation,
    isTranslating,
    currentLanguage,
    isEnabled: enabled && currentLanguage !== 'en'
  };
};

export default useApiTranslation; 